import React, { ReactNode } from 'react';
import { Button, notification, Space } from 'antd';
import { Flex } from '../../common/Flex';
import { LeftOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import { EntryLoadBar } from '../../dashboard/network/flows/svg/EntryLoadBar';
import TimeUtils from '../../../common/util/TimeUtils';
import {
    CapacityModel,
    MeasureCreateRequestDto,
    MeasureDto,
    MeasureId,
    RegulationProposalAcceptRejectDto,
    Reply,
    TimePeriod,
} from 'app/model/CORE';
import { ModalHandler } from '../ModalHandler';
import { ServiceFactory } from 'app/service/ServiceFactory';
import {
    DetailedSolution,
    DetailedSolutionsReply,
    ExtendedMonitoredTv,
    MonitoredMcdmMeasure,
    Solution,
    SolutionType,
    TvMeteoAlert,
    TVTimePeriodSelection,
} from 'app/model/SOL49';
import { DCBSolverUtils } from 'app/common/util/DCBSolverUtils';
import AppContext from '../../../common/context/AppContext';
import { MeasureImpactPanel } from './MeasureImpactPanel';
import Utils from '../../../common/util/Utils';
import memoizeOne from 'memoize-one';
import { SolutionSummaryPanel } from 'app/components/panels/solver/SolutionSummaryPanel';
import './SolverPanel.less';
import { SolutionUtils } from 'app/common/util/SolutionUtils';
import { SidePanelSeparator } from '../../common/SidePanelSeparator';
import { CompareSolutionPanel } from 'app/components/panels/solver/CompareSolutionPanel';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { PartialDashboardDataUpdateEvent } from 'app/common/eventbus/Events';
import { EntityType } from 'app/model/client/SOL49';
import { MapPanelLayerOptions } from 'app/components/panels/map/MapPanel';
import * as data from 'app/components/panels/solver/solution_details_reply_mock';
import produce from 'immer';
import {
    AbstractSolverPanel,
    AbstractSolverPanelProps,
    AbstractSolverPanelState,
    solverConnector,
} from './AbstractSolverPanel';

const MOCK_DETAILED_SOLUTION = false;

const getSortedSolutions = memoizeOne((solutions: Array<DetailedSolution>) => {
    return solutions?.sort(SolutionUtils.compare);
});

interface SolverPanelProps extends AbstractSolverPanelProps {
    tvs: Array<TVTimePeriodSelection>; // tvIds should be provided - timePeriod can optionally be specified
    proposedMeasures?: MonitoredMcdmMeasure[];
}

enum SolverMode {
    COMPARE,
    STUDY,
}

interface SolverPanelState extends AbstractSolverPanelState {
    monitoredTvs: Array<ExtendedMonitoredTv>;
    monitoredPeriod: TimePeriod;
    tvSelection: Array<TVTimePeriodSelection>;
    tvMeteoAlerts: { [key: string]: Array<TvMeteoAlert> };
    applicableScenarios: Map<string, number>; // Number of flights concerned in each scenario
    outdatedSolutions: Array<string>;
    mode: SolverMode;
    sidePanelContentVisible: boolean;
}

const SHOW_NIA_IMPACTED_EXTRA: boolean = false;

const MAP_LAYER_OPTIONS: MapPanelLayerOptions = {
    measuresLayerEnabled: true,
    tvImpactLayerEnabled: true,
    weatherLayerEnabled: true,
    showNiaImpactedExtra: SHOW_NIA_IMPACTED_EXTRA,
};

export class SolverPanel extends AbstractSolverPanel<SolverPanelProps, SolverPanelState> {
    static MAIN_PANEL_WIDTH_EM = 27;
    static SIDE_PANEL_WIDTH_EM = 83;
    static EXTRA_PANEL_WIDTH_EM = 45;
    static MAP_EXTRA_PANEL_WIDTH_EM = 100;
    static COMPARE_PANEL_WIDTH_EM = 163;
    public static INITIAL_WIDTH = SolverPanel.MAIN_PANEL_WIDTH_EM + 'em';

    constructor(props: SolverPanelProps) {
        super(props);
        this.state = Object.assign(this.state, {
            showSeparator: false,
            outdatedSolutions: [],
            mode: SolverMode.STUDY,
            sidePanelContentVisible: true,
        });
    }

    componentDidMount() {
        this.getTvLoadAndPeriods();
        super.componentDidMount();
    }

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    protected getMapLayerOptions(): MapPanelLayerOptions {
        return MAP_LAYER_OPTIONS;
    }

    getTvLoadAndPeriods = () => {
        this.setState({ loading: true });
        ServiceFactory.sol49Service()
            .getMonitoringData({
                capacityModel: CapacityModel.CCM,
                convectionSource: AppContext.getConvectionSource(),
                //include the monitored period in order to work with pretact (server assumes if no timperiod then is current time)
                tvsWithTimePeriod: this.props.tvs.map(tvPeriod => ({
                    tvId: tvPeriod.tvId,
                    timePeriod: Utils.isExists(tvPeriod.timePeriod)
                        ? tvPeriod.timePeriod
                        : AppContext.getMonitoringPeriod(),
                })),
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    const tvSelection = DCBSolverUtils.getAutoTvSelection(
                        reply.data.monitoredTvs,
                        TimeUtils.addMinutes(TimeUtils.withoutSeconds(TimeUtils.NOW()), 30),
                        this.props.tvs,
                    );
                    this.setState(
                        {
                            monitoredTvs: reply.data.monitoredTvs,
                            monitoredPeriod: this.getMonitoringPeriod(tvSelection),
                            tvSelection: tvSelection,
                            tvMeteoAlerts: reply.data.tvMeteoAlerts,
                        },
                        () => this.fetchSolutions(),
                    );
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };

    fetchSolutions = () => {
        if (!this.state.tvSelection) return;

        if (!this.state.loading) {
            this.setState({ loading: true });
        }

        ServiceFactory.sol49Service()
            .getSolutions({
                monitoredPeriod: this.state.monitoredPeriod,
                capacityModel: CapacityModel.CCM,
                tvAndPeriods: this.state?.tvSelection,
                proposedMeasures: this.props.proposedMeasures?.map(m => m.measure),
                dataset: AppContext.getDataset(),
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    if (reply.data.errors?.length) {
                        notification.error({
                            message: 'Error',
                            description: 'Find solutions returned errors - check console',
                            duration: 0,
                        });
                        console.log('Find solutions returned errors: ', reply.data.errors);
                    }
                    this.setState({ solverSolutions: reply.data.solutions, outdatedSolutions: [] });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => {
                this.setState({ loading: false }, () => {
                    if (this.props.proposedMeasures) {
                        this.fetchDetailedSolutions([SolutionUtils.getInitialSolution(this.state.solverSolutions)]);
                    }
                });
            });
    };

    mockReply() {
        const reply: Reply<DetailedSolutionsReply> = JSON.parse(data.data);

        if (reply.data.errors?.length) {
            notification.error({
                message: 'Error',
                description: 'Find detailed solutions returned errors - check console',
                duration: 0,
            });
            console.log('Find detailed solutions returned errors: ', reply.data.errors);
        }

        // Combine solutions that have already been fetched with the detailed ones that have just been received
        const combinedSolutions = reply.data.solutions;

        this.setState(
            {
                solverSolutions: combinedSolutions,
                outdatedSolutions: [],
                solverSelectedSolutionIdx: 0,
                solverSelectedRrtFlights: [], //reset
            },
            () => {
                this.showSidePanel(this.state.mode === SolverMode.STUDY ? 3 : 2);
                this.props.setDetailedSolutions(combinedSolutions.filter(el => Utils.isExists(el.solutionImpact)));
            },
        );
    }

    fetchDetailedSolutions = (solutions: Array<Solution>) => {
        if (MOCK_DETAILED_SOLUTION) {
            this.mockReply();
            return;
        }
        if (!this.state.tvSelection) return;

        this.setState({ loading: true, applicableScenarios: undefined });

        ServiceFactory.sol49Service()
            .getDetailedSolutions({
                solutions: solutions,
                monitoredPeriod: this.state.monitoredPeriod,
                capacityModel: CapacityModel.CCM,
                tvAndPeriods: this.state?.tvSelection,
                dataset: AppContext.getDataset(),
                includeNiaImpactedExtra: SHOW_NIA_IMPACTED_EXTRA,
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                    console.log('Find detailed solutions error: ', reply.error);
                } else if (Utils.isNotEmpty(reply.data.errors)) {
                    notification.error({
                        message: 'Error',
                        description: 'Find detailed solutions returned errors - check console',
                        duration: 0,
                    });
                    console.log('Find detailed solutions returned errors: ', reply.data.errors);
                } else {
                    // Combine solutions that have already been fetched with the detailed ones that have just been received
                    const combinedSolutions = this.state.solverSolutions.slice();
                    const outdatedSolutions = this.state.outdatedSolutions.slice();
                    for (const solution of reply.data.solutions) {
                        const solIdx = combinedSolutions.findIndex(value => value.name === solution.name);
                        if (solIdx >= 0) {
                            combinedSolutions[solIdx] = solution;
                        } else {
                            combinedSolutions.push(solution); // Should never happen...
                        }

                        // Remove solution for the list of outdated
                        const outdatedSolIdx = outdatedSolutions.findIndex(name => name === solution.name);
                        if (outdatedSolIdx >= 0) {
                            outdatedSolutions.splice(outdatedSolIdx, 1);
                        }
                    }

                    // Select the solution that has been computed, or the first one if many
                    let selectedSolutionIdx = 0;
                    if (solutions?.length === 1) {
                        selectedSolutionIdx = combinedSolutions.findIndex(value => value.name === solutions[0].name);
                    }

                    this.setState(
                        {
                            solverSolutions: combinedSolutions,
                            outdatedSolutions: outdatedSolutions,
                            solverSelectedSolutionIdx: selectedSolutionIdx,
                            solverSelectedRrtFlights: [], //reset
                        },
                        () => {
                            this.showSidePanel(this.state.mode === SolverMode.STUDY ? 3 : 2);
                            this.props.setDetailedSolutions(
                                combinedSolutions.filter(el => Utils.isExists(el.solutionImpact)),
                            );
                        },
                    ); //avoid storing not detailed solutions
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };

    private getMonitoringPeriod(tvSelection: TVTimePeriodSelection[]) {
        const monitoredPeriod = AppContext.getMonitoringPeriod();
        // Monitoring period should also cover SelectedPeriods (in case of regulation proposal > MonitoringPeriod.to)
        tvSelection
            .map(tvSelection => tvSelection.timePeriod)
            .forEach(period => TimeUtils.growToCover(monitoredPeriod, period));
        return monitoredPeriod;
    }

    computeAll = () => {
        // Only fetch solutions that have not been computed yet
        const solutions = this.state.solverSolutions.filter(solution => !Utils.isExists(solution.impactSummary));
        this.fetchDetailedSolutions(solutions);
    };

    updateSolution = (solution: DetailedSolution) => {
        // Init new solution
        const updatedSolution: Solution = {
            name: solution.name,
            type: solution.type,
            measures: solution.measures.slice(),
            tvs: solution.tvs.slice(),
        };
        if (SolutionUtils.isInitialSolution(solution)) {
            // Set custom name and type
            updatedSolution.name = 'NM PROPOSAL';
            updatedSolution.type = SolutionType.CUSTOM;

            // Restore the initial proposed solution measures
            const solutions = this.state.solverSolutions.slice();
            const initialSolution = solutions.find(s => SolutionUtils.isInitialSolution(s));
            initialSolution.measures = Utils.deepCopy(
                this.props.proposedMeasures.map(monitoredMeasures => monitoredMeasures.measure),
            );

            // Prevent multiple "copies" of "NM PROPOSAL"s
            let idx = solutions.findIndex(s => s.name === updatedSolution.name);
            if (idx === -1) {
                // Insert the new solution
                solutions.splice(1, 0, updatedSolution);
                idx = 1;
            } else {
                // Solution has been updated already, update it
                solutions[idx] = updatedSolution;
            }

            this.setState(
                {
                    solverSolutions: solutions,
                    solverSelectedSolutionIdx: idx,
                    // The proposed solution has been restored to initial state, remove it from the outdated ones
                    outdatedSolutions: Utils.copyAndRemoveIfNeeded(this.state.outdatedSolutions, solution.name),
                },
                () => this.fetchDetailedSolutions([updatedSolution]),
            );
        } else {
            this.fetchDetailedSolutions([updatedSolution]);
        }
    };

    applySolution = async (solution: Solution) => {
        if (!solution || !solution.measures) return;

        this.setState({ loading: true });

        const appliedSolutions = [];
        for (let measure of solution.measures) {
            try {
                let reply: Reply<MeasureDto>;
                if (MeasureUtils.isRegulationProposal(measure) && AppContext.isNmvpBaseline()) {
                    // TODO fix serverSide, NoDelayWindow should be null in CREATE/UPDATE ctx
                    measure = produce(measure, update => {
                        update.noDelayWindowInMs = -1;
                    });
                    reply = await ServiceFactory.stamCoreService().acceptRejectRegulationProposal({
                        acceptReject: RegulationProposalAcceptRejectDto.ACCEPT,
                        dataset: AppContext.getDataset(),
                        regulationProposal: measure,
                    });
                } else {
                    reply = await ServiceFactory.stamCoreService().createMeasure({
                        computeNetworkImpactAssessment: false,
                        dataset: AppContext.getDataset(),
                        measure: measure,
                        waitUntilApplied: true,
                    } as MeasureCreateRequestDto);
                }

                if (reply.error) {
                    notification.error({
                        message: 'Error',
                        description: (
                            <>
                                {reply.error.message} <br />
                                {reply.error.details}
                            </>
                        ),
                        duration: 0,
                    });
                } else {
                    appliedSolutions.push(reply.data);
                }
            } catch (reason) {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            }
        }

        // Update the state of measure proposals
        PartialDashboardDataUpdateEvent.fireEvent(EntityType.MEASURE_PROPOSAL);
        console.log(`${appliedSolutions.length}/${solution.measures.length} measures applied successfully`);
        if (appliedSolutions.length === solution.measures.length) {
            this.props.onClose();
        } else {
            this.setState({
                loading: false,
            });
        }
    };

    shareSolution = (solution: DetailedSolution) => {
        const solutions = [];
        // Add proposed solution
        if (this.props.proposedMeasures) {
            solutions.push(SolutionUtils.getInitialSolution(this.state.solverSolutions));
        }
        // Add selected solution
        solutions.push(solution);

        const computedSolutions = this.state.solverSolutions.filter(s => Utils.isExists(s.solutionImpact));
        ModalHandler.showShareSolutionPanel(solutions, computedSolutions, () => this.props.onClose());
    };

    handleSelectionChange = (solutionIdx: number, selected: boolean) => {
        const solution = this.state.solverSolutions[solutionIdx];
        if (!Utils.isExists(solution.impactSummary)) {
            // No details -> calculate
            this.fetchDetailedSolutions([solution]);
        } else {
            // Display/hide solution details
            if (selected) {
                this.setState({ solverSelectedSolutionIdx: solutionIdx, applicableScenarios: undefined }, () =>
                    this.showSidePanel(),
                );
            } else {
                this.setState({ solverSelectedSolutionIdx: undefined, applicableScenarios: undefined }, () =>
                    this.hideSidePanel(),
                );
            }
        }
    };

    handleTvSelectionChange = (tvSelection: Array<TVTimePeriodSelection>) => {
        this.setState(
            {
                tvSelection: tvSelection,
                solverSelectedSolutionIdx: undefined,
            },
            () => {
                this.hideSidePanel();
                this.fetchSolutions();
            },
        );
    };

    showPeriodSelectionModal = () => {
        ModalHandler.showTVTimePeriodSelection(
            this.state.monitoredTvs,
            this.state.tvSelection,
            this.state.tvMeteoAlerts,
            this.handleTvSelectionChange,
        );
    };

    getNbSelectedTvs = () => {
        if (!this.state.tvSelection?.length) return 0;
        return this.state.tvSelection.filter(tvSelection => tvSelection.selected).length;
    };

    handleRemoveFromSolution = (measure: MeasureDto, solutionIdx: number) => {
        const solution = this.state.solverSolutions[solutionIdx];
        const updatedMeasures = solution.measures.filter(m => m.measureId.id !== measure.measureId.id);
        const updatedSolution = { ...solution, measures: updatedMeasures };
        const updatedSolutions = this.state.solverSolutions?.map(s => (s.name === solution.name ? updatedSolution : s));

        // Mark solution as outdated (needs to be updated)
        const outdatedSolutions = this.state.outdatedSolutions.slice();
        Utils.addIfNeeded(outdatedSolutions, solution.name);

        this.setState({ solverSolutions: updatedSolutions, outdatedSolutions: outdatedSolutions });
    };

    handleAddToSolution = (measures: Array<MeasureDto>) => {
        if (Utils.isNotExists(this.state.solverSelectedSolutionIdx)) return;

        const selectedSolution = this.state.solverSolutions[this.state.solverSelectedSolutionIdx];
        // Add measures to solution
        const updatedMeasures = selectedSolution.measures.slice();
        updatedMeasures.push(...measures);
        const updatedSolution = { ...selectedSolution, measures: updatedMeasures };

        const updatedSolutions = this.state.solverSolutions?.map(s =>
            s.name === selectedSolution.name ? updatedSolution : s,
        );

        // Mark solution as outdated (needs to be updated)
        const outdatedSolutions = this.state.outdatedSolutions.slice();
        Utils.addIfNeeded(outdatedSolutions, selectedSolution.name);

        this.setState({ solverSolutions: updatedSolutions, outdatedSolutions: outdatedSolutions });
    };

    handleApplicableScenarios = (applicableScenarios: Map<string, number>) => {
        this.setState({ applicableScenarios: applicableScenarios });
    };

    /**
     * Updates the measures when they are edited.
     *
     * The measureId parameter is used to identify a measure when it's ID is updated.
     *
     * @param measureId
     * @param solutionIdx
     * @param measure
     */
    handleMeasureEdited = (measureId: MeasureId, solutionIdx: number, measure: MeasureDto) => {
        const editedSolution = this.state.solverSolutions[solutionIdx];
        if (!editedSolution) {
            console.error('Error editing solution idx:', solutionIdx, this.state.solverSolutions);
            return;
        }

        // Update measure in selected solution
        const updatedMeasures: MeasureDto[] = editedSolution.measures.map(m =>
            m.measureId === measureId ? measure : m,
        );
        const updatedSolution: DetailedSolution = { ...editedSolution, measures: updatedMeasures };

        // Update solutions
        const updatedSolutions = this.state.solverSolutions?.map(s =>
            s.name === editedSolution.name ? updatedSolution : s,
        );

        // Mark solution as outdated (needs to be updated)
        const outdatedSolutions = this.state.outdatedSolutions.slice();
        Utils.addIfNeeded(outdatedSolutions, editedSolution.name);

        this.setState({
            solverSolutions: updatedSolutions,
            outdatedSolutions: outdatedSolutions,
        });
    };

    toggleCompare = () => {
        if (this.state.mode === SolverMode.STUDY) {
            this.setState(
                {
                    mode: SolverMode.COMPARE,
                    solverSidePanelType: undefined,
                },
                () => this.showSidePanel(1),
            );
        } else {
            this.setState(
                {
                    mode: SolverMode.STUDY,
                },
                () => this.showSidePanel(1),
            );
        }
    };

    getPanels() {
        const panels = new Map<ReactNode, number>();
        panels.set(this.getMainPanelContent(), SolverPanel.MAIN_PANEL_WIDTH_EM);
        if (this.state.mode === SolverMode.STUDY) {
            panels.set(
                this.getSidePanelContent(),
                this.state.sidePanelContentVisible ? SolverPanel.SIDE_PANEL_WIDTH_EM : 0,
            );
            panels.set(this.getSidePanelContentButton(), 2);
            panels.set(
                this.getSolverExtraPanelContent(),
                ('map' === this.state.solverSidePanelType
                    ? SolverPanel.MAP_EXTRA_PANEL_WIDTH_EM
                    : SolverPanel.EXTRA_PANEL_WIDTH_EM) +
                    (this.state.sidePanelContentVisible ? 0 : SolverPanel.SIDE_PANEL_WIDTH_EM),
            );
        } else {
            panels.set(this.getComparePanelContent(), SolverPanel.COMPARE_PANEL_WIDTH_EM);
        }
        return panels;
    }

    getMainPanelContent = () => {
        const sortedSolutions = getSortedSolutions(this.state.solverSolutions);
        const computedSolutions = this.state.solverSolutions?.filter(s => Utils.isExists(s.solutionImpact));

        return (
            <Flex shrink={false} className="solver-panel" direction="vertical">
                {this.renderHeader()}
                <Flex
                    size="100em"
                    grow={true}
                    direction="vertical"
                    style={{ backgroundColor: '#191919' }}
                    className="overflow">
                    <Flex
                        size="2.5em"
                        style={{
                            margin: '0.5em',
                            backgroundColor: '#262626',
                            borderRadius: '0.5em',
                            alignItems: 'center',
                            padding: '0 1em 0 2em',
                        }}>
                        <Flex style={{ fontWeight: 'bold' }}>Simulation's target</Flex>
                        <Flex size="2em">
                            <SettingOutlined style={{ fontSize: '1.5em' }} />
                        </Flex>
                    </Flex>
                    <Flex size="2em" style={{ margin: '0.5em', justifyContent: 'center' }}>
                        <Space>
                            <Button onClick={this.computeAll}>COMPUTE ALL</Button>
                            <Button onClick={this.toggleCompare} disabled={Utils.size(computedSolutions) < 2}>
                                COMPARE
                            </Button>
                        </Space>
                    </Flex>
                    <Flex direction="vertical" style={{ overflowY: 'scroll' }}>
                        {sortedSolutions?.map((solution, idx) => {
                            const nbFlightsConcerned = this.state.applicableScenarios?.get(solution.name) || 0;
                            return (
                                <SolutionSummaryPanel
                                    key={`solution-${idx}`}
                                    solution={solution}
                                    selectedSolution={
                                        this.state.solverSelectedSolutionIdx &&
                                        sortedSolutions[this.state.solverSelectedSolutionIdx]
                                    }
                                    selected={this.state.solverSelectedSolutionIdx === idx}
                                    onSelectionChange={selected => this.handleSelectionChange(idx, selected)}
                                    nbFlightsConcerned={nbFlightsConcerned}
                                    onAddToSolution={this.handleAddToSolution}
                                />
                            );
                        })}
                    </Flex>
                </Flex>
            </Flex>
        );
    };

    getSidePanelContent = () => {
        const solution = this.state.solverSolutions?.[this.state.solverSelectedSolutionIdx];
        if (!solution || !this.state.sidePanelContentVisible) return null;

        return (
            <MeasureImpactPanel
                solution={solution}
                meteoAlerts={this.state.tvMeteoAlerts}
                solutionImpactOutdated={this.state.outdatedSolutions.findIndex(name => name === solution.name) >= 0}
                onRemoveMeasure={measure =>
                    this.handleRemoveFromSolution(measure, this.state.solverSelectedSolutionIdx)
                }
                onMeasureEdited={(measureId, measure) =>
                    this.handleMeasureEdited(measureId, this.state.solverSelectedSolutionIdx, measure)
                }
                onShowTVsImpact={this.handleShowTVsImpact}
                onShowMeasuresImpact={this.handleShowMeasuresImpact}
                onUpdateSolution={this.updateSolution}
                onApplySolution={this.applySolution}
                onApplicableScenarios={this.handleApplicableScenarios}
                onShareSolution={this.shareSolution}
                onShowOnMap={this.handleShowOnMap}
                onRrtFlightShowOnMap={this.handleRrtFlightShowOnMap}
            />
        );
    };

    getSidePanelContentButton = () => {
        if (Utils.isNotExists(this.state.solverSidePanelType) || this.state.solverSidePanelType !== 'map') return null;
        if (this.state.sidePanelContentVisible) {
            return (
                <Flex
                    className={'iconOnSpan'}
                    size={'1.5em'}
                    style={{
                        alignItems: 'center',
                        margin: '-2em',
                        backgroundColor: '#3A3A3A',
                        justifyContent: 'center',
                    }}
                    onClick={this.handleSidePanelContentVisibilityChange}>
                    <LeftOutlined />
                </Flex>
            );
        } else {
            return (
                <Flex
                    className={'iconOnSpan'}
                    size={'1.5em'}
                    style={{
                        alignItems: 'center',
                        margin: '-2em',
                        backgroundColor: '#3A3A3A',
                        justifyContent: 'center',
                    }}
                    onClick={this.handleSidePanelContentVisibilityChange}>
                    <RightOutlined />
                </Flex>
            );
        }
    };

    handleSidePanelContentVisibilityChange = () => {
        this.setState(
            prevState => ({
                sidePanelContentVisible: !prevState.sidePanelContentVisible,
            }),
            () => this.mapPanelRef.current.forceMapRender(),
        );
    };

    showSidePanel = (panelIdx?: number) => {
        //if panelIdx not provided and the current idx visible is zero, then show index 1, otherwise keep at it is
        panelIdx = Utils.isNotExists(panelIdx) && this.state.panelIdxVisible === 0 ? 1 : panelIdx;

        if (Utils.isExists(panelIdx)) {
            this.setState(
                {
                    panelIdxVisible: panelIdx,
                },
                () => this.props.onWidthChange?.(this.getWidth() + this.getUnit()),
            );
        }
    };

    getComparePanelContent = () => {
        const computedSolutions = this.state.solverSolutions.filter(s => Utils.isExists(s.solutionImpact));
        const rightSolution = this.state.solverSolutions.findIndex(
            (s, idx) => Utils.isExists(s.solutionImpact) && idx !== this.state.solverSelectedSolutionIdx,
        );

        return (
            <Flex direction="vertical">
                <Flex size="2em" style={{ alignSelf: 'center', fontSize: '1.5em', fontWeight: 'bold' }}>
                    COMPARE SOLUTIONS
                </Flex>
                <Flex>
                    <CompareSolutionPanel
                        defaultSolution={this.state.solverSelectedSolutionIdx}
                        solutions={computedSolutions}
                        meteoAlerts={this.state.tvMeteoAlerts}
                        outdatedSolutions={this.state.outdatedSolutions}
                        onRemoveMeasure={this.handleRemoveFromSolution}
                        onMeasureEdited={this.handleMeasureEdited}
                        onShowTVsImpact={this.handleShowTVsImpact}
                        onShowMeasuresImpact={this.handleShowMeasuresImpact}
                        onUpdateSolution={this.updateSolution}
                        onApplySolution={this.applySolution}
                        onApplicableScenarios={this.handleApplicableScenarios}
                        onShareSolution={this.shareSolution}
                    />
                    <SidePanelSeparator onHideSidePanel={this.toggleCompare} />
                    <CompareSolutionPanel
                        defaultSolution={rightSolution}
                        solutions={computedSolutions}
                        outdatedSolutions={this.state.outdatedSolutions}
                        meteoAlerts={this.state.tvMeteoAlerts}
                        onRemoveMeasure={this.handleRemoveFromSolution}
                        onMeasureEdited={this.handleMeasureEdited}
                        onShowTVsImpact={this.handleShowTVsImpact}
                        onShowMeasuresImpact={this.handleShowMeasuresImpact}
                        onUpdateSolution={this.updateSolution}
                        onApplySolution={this.applySolution}
                        onApplicableScenarios={this.handleApplicableScenarios}
                        onShareSolution={this.shareSolution}
                    />
                </Flex>
            </Flex>
        );
    };

    renderHeader = () => {
        if (this.state.monitoredTvs?.length === 1) {
            // Header to be displayed when DCB issue is being investigated for a single TV
            return this.renderSingleTvHeader(this.state.monitoredTvs[0]);
        }
        return this.renderMultipleTvHeader();
    };

    renderSingleTvHeader = (monitoredTv: ExtendedMonitoredTv) => {
        const monitoredPeriod = this.state.monitoredPeriod;
        const activePeriods: Array<TimePeriod> = monitoredTv.activePeriods;
        const tvId = monitoredTv.refLocation.id;
        const selectedPeriod = this.state.tvSelection.find(tvSelection => tvSelection.tvId === tvId)?.timePeriod;
        return (
            <Flex size="9em" direction="vertical" className={'header'}>
                <Flex size="2.5em">
                    <Flex direction="vertical">
                        <Flex style={{ fontWeight: 'bold', justifyContent: 'center', fontSize: '1.25em' }}>{tvId}</Flex>
                        <Flex style={{ justifyContent: 'center' }}>
                            Solutions simulation ({this.state?.solverSolutions?.length || 0})
                        </Flex>
                    </Flex>
                    <Flex size="2em">
                        <SettingOutlined style={{ fontSize: '1.5em' }} onClick={this.showPeriodSelectionModal} />
                    </Flex>
                </Flex>
                <Flex
                    size="2.5em"
                    direction="vertical"
                    style={{ width: '22em', backgroundColor: '#262626', padding: '1em', borderRadius: '0.5em' }}>
                    <EntryLoadBar
                        drawBottomLine={true}
                        loadValues={monitoredTv.loadValues}
                        currentTime={AppContext.getCurrentTime()}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={activePeriods}
                        defaultSelectedPeriod={selectedPeriod}
                        barHeightEm={1}
                        marginTopEm={0.5}
                        marginBottomEm={1}
                    />
                    {/*this.props.occupancyLoad && this.props.occupancyLoad.filter(occupancyValues =>
                            // Display occupancy if highest load is above defined value (displayOccupancy)
                            // and highest entry load is above another value (displayOccupancyForTv)
                            MonitoredLoadUtils.isOccupancyDisplayed(occupancyValues.occupancyLoadValues, this.props.displayOccupancy) &&
                            this.props.entryLoad && MonitoredLoadUtils.isEntryDisplayed(this.props.entryLoad, this.props.displayOccupancyForTv)
                        )
                            .map(occupancyValues => {
                                return <OccupancyLoadBar
                                    key={'occupancy-' + occupancyValues.durationInMin}
                                    drawBottomLine={true}
                                    loadValues={occupancyValues.occupancyLoadValues}
                                    activePeriods={this.props.activePeriods}
                                    currentTime={this.props.currentTime}
                                    monitoredPeriod={monitoredPeriod}
                                    occupancyPeriods={occupancyPeriods}
                                    barHeightEm={1}
                                />
                            })*/}
                    {/*tvMeasures && tvMeasures.map(measure => {
                            if (this.props.tvName) { // Display detailed load only if TVset is not colapsed (tvName is provided)
                                return (
                                    <MeasureLoadBar
                                        key={"measure-load-" + measure.measure.measureId.id}
                                        loadValues={measure.measureLoad}
                                        monitoredPeriod={monitoredPeriod}
                                        currentTime={this.props.currentTime}
                                        barHeightEm={0.5}/>
                                )
                            } else {
                                return (
                                    <MeasureLoadBar
                                        key={"measure-load-" + measure.measure.measureId.id}
                                        loadValues={measure.measureLoad}
                                        monitoredPeriod={monitoredPeriod}
                                        currentTime={this.props.currentTime}
                                        barHeightEm={0.1}
                                        fillColor='white'/>
                                )
                            }
                        })*/}
                </Flex>
            </Flex>
        );
    };

    renderMultipleTvHeader = () => {
        return (
            <Flex size="9em" direction="vertical" className={'header'}>
                <Flex size="2em">
                    <Flex direction="vertical">
                        <Flex style={{ fontWeight: 'bold', justifyContent: 'center', fontSize: '1.25em' }}>
                            Advanced Network Issue
                        </Flex>
                    </Flex>
                    <Flex size="2em">
                        <SettingOutlined style={{ fontSize: '1.5em' }} onClick={this.showPeriodSelectionModal} />
                    </Flex>
                </Flex>
                <Flex size="1em" direction="vertical" style={{ padding: '1em', alignItems: 'center' }}>
                    {this.getNbSelectedTvs()} TFV selected
                </Flex>
            </Flex>
        );
    };
}

export default solverConnector(SolverPanel);
